import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  Calendar,
  Plus,
  AlertTriangle,
  Loader2,
  Bot,
  Sparkles,
  Edit,
  Trash2,
  X,
  Check,
  Folder,
  FolderOpen,
  MoreVertical,
  Pin,
  PinOff,
  FolderPlus,
  Image,
  Users
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useProjects } from '../../../context/ProjectContext';
import { API_URL } from '../../../utils/config';
import { SessionManager } from '../../../utils/sessionManager';
import { apiGet, apiPatch, apiDelete, apiPost } from '../../../utils/api';
import {
  RenameChatModal,
  AddToProjectModal
} from '../../common/modals';

function ChatHistory() {
    const navigate = useNavigate();
    const { projects, fetchProjects, selectProject } = useProjects();
    const [searchTerm, setSearchTerm] = useState('');
    const [chats, setChats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dateFilterMode, setDateFilterMode] = useState('date'); // 'date', 'month', 'year'
    const [dateFilter, setDateFilter] = useState(null);
    const [projectFilter, setProjectFilter] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [editingChatId, setEditingChatId] = useState(null);
    const [editingChatTitle, setEditingChatTitle] = useState('');
    const [deletingChatId, setDeletingChatId] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const itemsPerPage = 10;

    // Chat menu and modals
    const [activeChatMenu, setActiveChatMenu] = useState(null);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showAddToProjectModal, setShowAddToProjectModal] = useState(false);
    const [selectedChat, setSelectedChat] = useState(null);

    const editInputRef = useRef(null);
    
    // Format date helper
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        });
    };
    
    // Fetch projects
    useEffect(() => {
        fetchProjects(true); // Include archived
    }, [fetchProjects]);

    // Fetch chats from the API
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch chats using apiGet with automatic token refresh
                const chatsResponse = await apiGet('/chats');

                if (chatsResponse.ok) {
                    const data = await chatsResponse.json();
                    if (data && data.chats) {
                        setChats(data.chats);
                    }
                }

                setLoading(false);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load chats. Please try again later.');
                setLoading(false);
            }
        };

        fetchData();
    }, []);
    
    // Filter chats based on search term, date filter, and project filter
    const filteredChats = chats.filter(chat => {
        const matchesSearch = !searchTerm ||
            chat.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            chat.preview.toLowerCase().includes(searchTerm.toLowerCase());

        // Apply date filter based on mode
        let matchesDate = true;
        if (dateFilter) {
            const chatDate = new Date(chat.updated_at).toISOString();
            if (dateFilterMode === 'date') {
                matchesDate = chatDate.slice(0, 10) === dateFilter;
            } else if (dateFilterMode === 'month') {
                matchesDate = chatDate.slice(0, 7) === dateFilter;
            } else if (dateFilterMode === 'year') {
                matchesDate = chatDate.slice(0, 4) === dateFilter;
            }
        }

        // Apply project filter if set
        const matchesProject = projectFilter === null || chat.project_id === projectFilter;

        return matchesSearch && matchesDate && matchesProject;
    }).sort((a, b) => {
        // Sort pinned chats to the top
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        // Then sort by updated_at
        return new Date(b.updated_at) - new Date(a.updated_at);
    });
    
    // Pagination
    const totalPages = Math.ceil(filteredChats.length / itemsPerPage);
    const paginatedChats = filteredChats.slice(
        (currentPage - 1) * itemsPerPage, 
        currentPage * itemsPerPage
    );
    
    // Determine the chat type based on session_id
    const getChatType = (sessionId) => {
        if (sessionId && sessionId.startsWith('advanced_ai_')) {
            return {
                type: 'advanced_ai',
                icon: <Sparkles className="h-4 w-4 text-purple-500" />,
                label: 'Advanced JPRO'
            };
        } else if (sessionId && sessionId.startsWith('ai_studio_')) {
            return {
                type: 'ai_studio',
                icon: <Image className="h-4 w-4 text-blue-500" />,
                label: 'JPRO Studio'
            };
        } else {
            return {
                type: 'standard',
                icon: <Bot className="h-4 w-4 text-blue-500" />,
                label: 'JPRO'
            };
        }
    };
    
    // Handle editing chat title
    const startEditingTitle = (chat) => {
        setEditingChatId(chat.id);
        setEditingChatTitle(chat.title);
        // Focus on the input after it's rendered
        setTimeout(() => {
            if (editInputRef.current) {
                editInputRef.current.focus();
            }
        }, 50);
    };
    
    const saveEditedTitle = async () => {
        if (!editingChatId || !editingChatTitle.trim()) return;

        try {
            // Using apiPatch with automatic token refresh
            await apiPatch(`/chats/${editingChatId}`, {
                chat: { title: editingChatTitle.trim() }
            });

            // Update local state
            setChats(prevChats =>
                prevChats.map(chat =>
                    chat.id === editingChatId
                        ? { ...chat, title: editingChatTitle.trim() }
                        : chat
                )
            );

            // Reset editing state
            setEditingChatId(null);
            setEditingChatTitle('');
        } catch (err) {
            console.error('Error updating chat title:', err);
            setError('Failed to update chat title. Please try again.');
        }
    };
    
    // Tag management functionality removed
    
    // Function to continue a chat session
    const continueChat = (chat) => {
        // Determine chat type and appropriate route based on session_id
        if (chat.session_id && chat.session_id.startsWith('ai_studio_')) {
            // Navigate to Imagen component with AI Studio tab
            navigate(`/dashboard/imagen?chat_id=${chat.id}&tab=ai-studio`);
            
            SessionManager.saveSessionRedundantly({
                chatId: chat.id.toString(),
                sessionId: chat.session_id,
                chatType: 'ai_studio'
            });
        } else {
            // All other chats (advanced_ai_ and standard) go to UnifiedChat
            navigate(`/dashboard/unified-chat?chat_id=${chat.id}`);
            
            const chatType = chat.session_id && chat.session_id.startsWith('advanced_ai_') 
                ? 'advanced' 
                : 'unified';
                
            SessionManager.saveSessionRedundantly({
                chatId: chat.id.toString(),
                sessionId: chat.session_id,
                chatType: chatType
            });
        }
    };
    
    // Function to start chat deletion process - shows confirmation dialog
    const startDeleteChat = (chatId, e) => {
        // Prevent click from bubbling to parent (which would navigate to chat)
        e.stopPropagation();
        
        // Set the chat ID to delete and show confirmation dialog
        setDeletingChatId(chatId);
        setShowDeleteConfirm(true);
    };
    
    // Function to cancel chat deletion
    const cancelDeleteChat = () => {
        setDeletingChatId(null);
        setShowDeleteConfirm(false);
    };
    
    // Function to confirm and execute chat deletion
    const confirmDeleteChat = async () => {
        if (!deletingChatId) return;

        try {
            // Using apiDelete with automatic token refresh
            await apiDelete(`/chats/${deletingChatId}`);

            // Update the local state by removing the deleted chat
            setChats(prevChats => prevChats.filter(chat => chat.id !== deletingChatId));

            // Reset deletion state
            setDeletingChatId(null);
            setShowDeleteConfirm(false);
        } catch (err) {
            console.error('Error deleting chat:', err);
            setError('Failed to delete chat. Please try again.');

            // Reset deletion state on error too
            setDeletingChatId(null);
            setShowDeleteConfirm(false);
        }
    };

    // Rename chat
    const handleRenameChat = async (newTitle) => {
        if (!selectedChat) return;

        try {
            const response = await apiPatch(`/chats/${selectedChat.id}`, {
                chat: { title: newTitle }
            });

            if (response.ok) {
                setChats(prevChats =>
                    prevChats.map(c =>
                        c.id === selectedChat.id ? { ...c, title: newTitle } : c
                    )
                );
                setShowRenameModal(false);
                setSelectedChat(null);
            }
        } catch (err) {
            console.error('Failed to rename chat:', err);
            setError('Failed to rename chat');
        }
    };

    // Add to project
    const handleAddToProject = async (projectId) => {
        if (!selectedChat) return;

        try {
            const response = await apiPatch(`/chats/${selectedChat.id}`, {
                chat: { project_id: projectId }
            });

            if (response.ok) {
                setChats(prevChats =>
                    prevChats.map(c =>
                        c.id === selectedChat.id ? { ...c, project_id: projectId } : c
                    )
                );
                setShowAddToProjectModal(false);
                setSelectedChat(null);
            }
        } catch (err) {
            console.error('Failed to add to project:', err);
            setError('Failed to add to project');
        }
    };

    // Toggle pin
    const handleTogglePin = async (chat) => {
        try {
            const response = await apiPost(`/chats/${chat.id}/toggle_pin`);

            if (response.ok) {
                const data = await response.json();
                setChats(prevChats =>
                    prevChats.map(c =>
                        c.id === chat.id ? { ...c, pinned: data.chat.pinned } : c
                    )
                );
            }
        } catch (err) {
            console.error('Failed to toggle pin:', err);
            setError('Failed to pin/unpin chat');
        }
    };

    return (
        <div className="space-y-6 p-4 max-w-screen-2xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <h1 className="text-2xl font-dm-serif font-bold">Chat History</h1>
                
                <div className="flex flex-wrap gap-2">
                    {/* Search input */}
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search conversations..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 border rounded-lg w-full md:w-64 bg-gray-200 dark:bg-gray-800 dark:border-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-rasa"
                        />
                        <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                    </div>
                    
                    {/* Date filter */}
                    <div className="flex items-center gap-1">
                        <select
                            value={dateFilterMode}
                            onChange={(e) => {
                                setDateFilterMode(e.target.value);
                                setDateFilter(null);
                                setCurrentPage(1);
                            }}
                            className="px-2 py-2 border rounded-lg bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white font-rasa text-sm"
                        >
                            <option value="date">Exact Date</option>
                            <option value="month">Month</option>
                            <option value="year">Year</option>
                        </select>
                        <div className="relative flex items-center">
                            <Calendar size={18} className="absolute left-3 text-gray-400 pointer-events-none" />
                            <input
                                type={dateFilterMode === 'year' ? 'number' : dateFilterMode === 'month' ? 'month' : 'date'}
                                value={dateFilter || ''}
                                onChange={(e) => {
                                    setDateFilter(e.target.value || null);
                                    setCurrentPage(1);
                                }}
                                {...(dateFilterMode === 'year' ? { min: 2020, max: 2030, placeholder: 'YYYY' } : {})}
                                className="pl-10 pr-3 py-2 border rounded-lg bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-rasa w-40"
                            />
                            {dateFilter && (
                                <button
                                    onClick={() => { setDateFilter(null); setCurrentPage(1); }}
                                    className="ml-1 p-1.5 rounded-lg text-gray-400 dark:text-gray-100 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    title="Clear date filter"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Project filter */}
                    <select
                        value={projectFilter === null ? '' : projectFilter}
                        onChange={(e) => setProjectFilter(e.target.value === '' ? null : parseInt(e.target.value))}
                        className="px-3 py-2 border rounded-lg bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 font-rasa"
                    >
                        <option value="">All Chats</option>
                        {projects.filter(p => !p.archived).map(project => (
                            <option key={project.id} value={project.id}>
                                {project.name}
                            </option>
                        ))}
                    </select>
                    
                    {/* New chat button */}
                    <button
                        onClick={() => {
                            // Clear project context so new chat isn't tied to last visited project
                            selectProject(null);
                            // Clear all storage first to ensure a fresh chat
                            localStorage.removeItem('unified_current_chat_id');
                            localStorage.removeItem('unified_current_session');
                            localStorage.removeItem('unified_rag_session_id');
                            localStorage.removeItem('jpro_current_chat_id');
                            localStorage.removeItem('jpro_current_session');
                            SessionManager.clearSessionRedundantly();

                            // Navigate to a clean URL
                            navigate('/dashboard/unified-chat');
                        }}
                        className="flex items-center space-x-1 px-3 py-2 border text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:text-gray-800 transition-colors font-dm-serif"
                    >
                        <Plus size={18} />
                        <span>New Chat</span>
                    </button>
                </div>
            </div>

            {/* Error message if applicable */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                        <AlertTriangle size={18} />
                        <p className="font-medium">{error}</p>
                    </div>
                </div>
            )}

            {/* Chat list */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 divide-y dark:divide-gray-700 max-h-[calc(100vh-16rem)] sm:max-h-[calc(100vh-14rem)] md:max-h-[calc(100vh-12rem)] lg:max-h-[calc(100vh-10rem)] overflow-y-auto">
                {loading ? (
                    <div className="p-8 text-center">
                        <Loader2 className="animate-spin h-8 w-8 mx-auto text-blue-500 mb-4" />
                        <p className="text-gray-500 dark:text-gray-400 font-rasa">Loading conversations...</p>
                    </div>
                ) : paginatedChats.length > 0 ? (
                    paginatedChats.map(chat => (
                        <div key={chat.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors relative">
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2 flex-1">
                                    <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0" />
                                    {chat.pinned && (
                                        <Pin className="h-3.5 w-3.5 text-orange-500 fill-orange-500 flex-shrink-0" />
                                    )}
                                    {/* Show collaborative indicator if chat belongs to a collaborative project */}
                                    {chat.project_id && projects.find(p => p.id === chat.project_id)?.is_collaborative && (
                                        <Users className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" title="Collaborative chat" />
                                    )}
                                    <h3 className="font-dm-serif font-medium truncate" onClick={() => continueChat(chat)}>
                                        {chat.title}
                                    </h3>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(chat.updated_at)}</span>
                                    {/* Menu button */}
                                    <div className="relative">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveChatMenu(activeChatMenu === chat.id ? null : chat.id);
                                            }}
                                            className="p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                        >
                                            <MoreVertical className="w-4 h-4" />
                                        </button>

                                        {/* Dropdown menu */}
                                        {activeChatMenu === chat.id && (
                                            <div className="absolute right-0 mt-1 w-48 rounded-lg shadow-lg border z-10 bg-white dark:bg-gray-800 dark:border-gray-700">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedChat(chat);
                                                        setShowRenameModal(true);
                                                        setActiveChatMenu(null);
                                                    }}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm font-rasa hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                >
                                                    <Edit size={14} />
                                                    Rename
                                                </button>
                                                {!chat.project_id && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedChat(chat);
                                                            setShowAddToProjectModal(true);
                                                            setActiveChatMenu(null);
                                                        }}
                                                        className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm font-rasa hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                    >
                                                        <FolderPlus size={14} />
                                                        Add to Project
                                                    </button>
                                                )}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleTogglePin(chat);
                                                        setActiveChatMenu(null);
                                                    }}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm font-rasa hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                >
                                                    {chat.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                                                    {chat.pinned ? 'Unpin' : 'Pin'}
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        startDeleteChat(chat.id, e);
                                                        setActiveChatMenu(null);
                                                    }}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm font-rasa hover:bg-gray-50 dark:hover:bg-gray-700 text-red-500 transition-colors"
                                                >
                                                    <Trash2 size={14} />
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 line-clamp-2 font-rasa" onClick={() => continueChat(chat)}>{chat.preview}</p>

                            {/* Project badge */}
                            {chat.project_id && (() => {
                                const project = projects.find(p => p.id === chat.project_id);
                                return project ? (
                                    <div className="flex items-center gap-1 mb-2">
                                        <div
                                            className="w-2 h-2 rounded-full"
                                            style={{ backgroundColor: project.color }}
                                        />
                                        <Folder className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                        <span className="text-xs text-gray-500 dark:text-gray-400 font-rasa">
                                            {project.name}
                                        </span>
                                    </div>
                                ) : null;
                            })()}

                            <div className="flex justify-between items-center">
                                <button
                                    onClick={() => continueChat(chat)}
                                    className="text-blue-500 hover:text-blue-600 text-sm font-medium font-dm-serif"
                                >
                                    Continue Chat
                                </button>
                                <span className="text-xs text-gray-500 dark:text-gray-400">{chat.message_count} messages</span>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400 font-rasa">
                        {searchTerm || dateFilter ? (
                            <p>No chats found matching your filters</p>
                        ) : (
                            <p>No chat history available. Start a new conversation!</p>
                        )}
                    </div>
                )}
            </div>

            {/* Pagination */}
            {!loading && totalPages > 1 && (() => {
                const maxVisible = 7;
                const half = Math.floor(maxVisible / 2);
                let start = Math.max(1, currentPage - half);
                let end = Math.min(totalPages, start + maxVisible - 1);
                if (end - start + 1 < maxVisible) {
                    start = Math.max(1, end - maxVisible + 1);
                }

                const pages = [];
                if (start > 1) {
                    pages.push(1);
                    if (start > 2) pages.push('...');
                }
                for (let i = start; i <= end; i++) {
                    pages.push(i);
                }
                if (end < totalPages) {
                    if (end < totalPages - 1) pages.push('...');
                    pages.push(totalPages);
                }

                return (
                    <div className="flex justify-center mt-6">
                        <nav className="flex items-center space-x-1 font-rasa">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className={`px-3 py-1 rounded-md border dark:border-gray-700 ${
                                    currentPage === 1
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                            >
                                Previous
                            </button>

                            {pages.map((page, idx) => (
                                page === '...' ? (
                                    <span key={`ellipsis-${idx}`} className="px-2 py-1 text-gray-400">...</span>
                                ) : (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={`px-3 py-1 rounded-md ${
                                            currentPage === page
                                                ? 'bg-blue-500 text-white'
                                                : 'border dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                )
                            ))}

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className={`px-3 py-1 rounded-md border dark:border-gray-700 ${
                                    currentPage === totalPages
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                );
            })()}
            
            {/* Delete Confirmation Dialog */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold mb-4 font-dm-serif">Delete Chat?</h3>
                        <p className="mb-6 text-gray-600 dark:text-gray-300 font-rasa">
                            Are you sure you want to delete this chat? This action cannot be undone and all messages will be permanently removed.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={cancelDeleteChat}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteChat}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                            >
                                Delete Chat
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modals */}
            <RenameChatModal
                isOpen={showRenameModal}
                onClose={() => {
                    setShowRenameModal(false);
                    setSelectedChat(null);
                }}
                chatTitle={selectedChat?.title || ''}
                onRename={handleRenameChat}
            />

            <AddToProjectModal
                isOpen={showAddToProjectModal}
                onClose={() => {
                    setShowAddToProjectModal(false);
                    setSelectedChat(null);
                }}
                projects={projects}
                onAdd={handleAddToProject}
            />

            {/* Click outside to close menu */}
            {activeChatMenu && (
                <div
                    className="fixed inset-0 z-0"
                    onClick={() => setActiveChatMenu(null)}
                />
            )}
        </div>
    );
}

export default ChatHistory;